import React, { useCallback, useState } from 'react';

import { formatPhoneNumber } from '@eika-infrastruktur/format';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { WarningCard, SuccessCard } from '@eika/card';
import { IconTypes } from '@eika/icon';
import { Input } from '@eika/input';
import { Label } from '@eika/label';
import { RichIconTypes } from '@eika/rich-icon';

import { useConfig } from '../../../defaultConfig';
import type { UpdateManagerPayload, ManagerId } from '../../../generated-types';
import useDebounce from '../../../hooks/useDebounce';
import useTranslation from '../../../hooks/useTranslation';
import { isValidEmail, isValidPhoneNumber } from '../../ContactInformation/form/validate';
import { cleanupPhoneNumber } from '../../ContactInformation/format';

type Props = {
    id?: ManagerId;
    emailState: [string | undefined, React.Dispatch<React.SetStateAction<string | undefined>>];
    mobileState: [string, React.Dispatch<React.SetStateAction<string>>];
    isLoading?: boolean;
    isSuccess?: boolean;
    onSubmit: (id: ManagerId, payload: UpdateManagerPayload) => void;
    error?: RequestExceptionWithResponse | null;
    reset: () => void;
};

export default ({
    id,
    error,
    onSubmit,
    emailState,
    mobileState,
    isLoading = false,
    isSuccess = true,
    reset,
}: Props) => {
    const translated = useTranslation();

    const [emailError, setEmailError] = useState<string>();
    const [mobilError, setMobileError] = useState<string>();
    const [email, setEmail] = emailState;
    const [mobile, setMobile] = mobileState;

    const {
        featureToggles: { enableUpdatingGeneralManager },
    } = useConfig();

    const validateEmail = useCallback(
        (value: string) =>
            setEmailError(
                value.length > 0 && !isValidEmail(value) ? translated.companyInfo.errors.invalidEmail : undefined,
            ),
        [setEmailError, translated.companyInfo.errors.invalidEmail],
    );

    const validatePhoneNumber = useCallback(
        (value: string) => {
            const cleanedValue = cleanupPhoneNumber(value);
            const error =
                value.length > 0 && !isValidPhoneNumber(cleanedValue)
                    ? translated.companyInfo.errors.invalidMobile
                    : undefined;

            setMobileError(error);
        },
        [setMobileError, translated.companyInfo.errors.invalidMobile],
    );

    const debounceValidateEmail = useDebounce(validateEmail, 600);
    const debounceValidateMobile = useDebounce(validatePhoneNumber, 600);

    const changeEmail = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setEmail(e.currentTarget.value);
            debounceValidateEmail(e.currentTarget.value);
        },
        [setEmail, debounceValidateEmail],
    );

    const changeMobile = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setMobile(e.currentTarget.value);
            debounceValidateMobile(e.currentTarget.value);
        },
        [setMobile, debounceValidateMobile],
    );

    const blurChangeMobile = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            setMobile(formatPhoneNumber(e.target.value));
            validatePhoneNumber(e.target.value);
        },
        [setMobile, validatePhoneNumber],
    );

    const submitOrRetry = useCallback(() => {
        const cleanedPhoneNumber = cleanupPhoneNumber(mobile);
        if (
            !id ||
            !enableUpdatingGeneralManager ||
            ((email ?? '')?.length > 0 && !isValidEmail(email)) ||
            ((mobile ?? '')?.length > 0 && !isValidPhoneNumber(cleanedPhoneNumber))
        ) {
            return;
        }

        onSubmit(id, { email: email ?? '', phoneNumber: cleanedPhoneNumber });
    }, [id, email, mobile, enableUpdatingGeneralManager, onSubmit]);

    const submit = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            validateEmail(email ?? '');
            validatePhoneNumber(mobile ?? '');

            submitOrRetry();
        },
        [email, mobile, validateEmail, validatePhoneNumber, submitOrRetry],
    );

    if (error) {
        return (
            <WarningCard
                className="update-general-manager__error-wrapper"
                title={translated.errors.ohNo}
                isModal
                icon={RichIconTypes.UNDRENDE}
            >
                <div className="contact-information__sign-error-message">
                    <p className="typography-paragraph">{translated.contactInfo.signingError.message}</p>
                    <p className="typography-paragraph">{translated.contactInfo.signingError.contactMessage}</p>
                </div>
                <div className="error-button-wrapper">
                    <LinkButton onClick={reset}>{translated.cancel}</LinkButton>

                    <ButtonWithSpinner showSpinner={isLoading} buttonType="positive" onClick={submitOrRetry}>
                        {translated.tryAgain}
                    </ButtonWithSpinner>
                </div>
            </WarningCard>
        );
    }

    if (isSuccess) {
        return <SuccessCard centeredWithMargin title={translated.companyInfo.updateSuccess} />;
    }

    if (!id) {
        return (
            <WarningCard
                title={translated.errors.somethingWentWrong}
                className="update-general-manager__error-wrapper"
                isModal
                icon={RichIconTypes.UNDRENDE}
            >
                <div className="contact-information__sign-error-message">
                    <p className="typography-paragraph">{translated.companyInfo.generalManagerNotFound}</p>
                </div>
            </WarningCard>
        );
    }

    return (
        <>
            <section title="beskrivelse">
                <p className="update-general-manager__description">{translated.companyInfo.editDescription}</p>
                <Label type="info" text={translated.companyInfo.editNameWarning} icon={IconTypes.UTROPSTEGN_24} />
            </section>
            <form noValidate onSubmit={submit} className="update-general-manager__form">
                <Input
                    label={translated.contactInfo.email}
                    type="email"
                    value={email ?? ''}
                    onChange={changeEmail}
                    error={emailError}
                    disabled={isLoading || !enableUpdatingGeneralManager}
                    onBlur={(e) => validateEmail(e.target.value)}
                />
                <Input
                    label={translated.contactInfo.mobile}
                    type="tel"
                    value={mobile ?? ''}
                    onChange={changeMobile}
                    error={mobilError}
                    disabled={isLoading || !enableUpdatingGeneralManager}
                    onBlur={blurChangeMobile}
                />

                <div className="update-general-manager__form-submit">
                    <ButtonWithSpinner
                        disabled={!enableUpdatingGeneralManager}
                        buttonType="positive"
                        showSpinner={isLoading}
                        type="submit"
                    >
                        {translated.save}
                    </ButtonWithSpinner>
                </div>
            </form>
        </>
    );
};
